import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Eye,
  EyeOff,
  Edit2,
  Trash2,
  Filter,
  MoreVertical,
  Calendar,
  Clock,
  LayoutGrid,
  FileText,
  AlertCircle,
} from "lucide-react";
import { usePosts } from "@/hooks/blog/usePosts";
import { useCategories } from "@/hooks/blog/useCategories";
import { useTogglePublishPost } from "@/hooks/blog/useEditPost";
import { useDeletePost } from "@/hooks/blog/useDeletePost";
import { useSearch } from "@/hooks/useSearch";
import { getImageUrl } from "@/api/upload";
import { Button } from "@/components/ui/button";
import SearchInput from "@/components/shared/SearchInput";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";

const PAGE_SIZE = 3;

export default function BlogList() {
  // Component to conditionally show tooltip only when text is truncated
  const TextWithTooltip = ({ text, lineClamp = "line-clamp-1", className = "" }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        if (textRef.current) {
          const isClamped = textRef.current.scrollHeight > textRef.current.clientHeight;
          setIsTruncated(isClamped);
        }
      };

      checkTruncation();
      window.addEventListener("resize", checkTruncation);
      return () => window.removeEventListener("resize", checkTruncation);
    }, [text]);

    const textElement = (
      <div
        ref={textRef}
        className={`${lineClamp} ${className}`}
        style={{ cursor: isTruncated ? "help" : "default" }}>
        {text}
      </div>
    );

    if (!isTruncated) {
      return textElement;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{textElement}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md">
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  const [selectedCategory, setSelectedCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, postId: null, title: "" });

  const handleSearchChange = useCallback(() => {
    // Reset when search changes
  }, []);

  const { searchTerm, formattedDateTerm, isSearching, handleSearch, resetSearch } = useSearch({
    onSearchChange: handleSearchChange,
  });

  const queryParams = useMemo(() => {
    const params = {
      page: 1,
      limit: visibleCount,
      search: formattedDateTerm || searchTerm || undefined,
      categoryId: selectedCategory || undefined,
    };
    if (filterStatus === "published") params.isPublished = true;
    if (filterStatus === "draft") params.isPublished = false;
    return params;
  }, [visibleCount, searchTerm, formattedDateTerm, selectedCategory, filterStatus]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchTerm, formattedDateTerm, selectedCategory, filterStatus]);

  const { data: postsData, isLoading, isFetching } = usePosts(queryParams);
  const { data: categoriesData } = useCategories();
  const deletePostMutation = useDeletePost();
  const togglePublishMutation = useTogglePublishPost();

  const posts = postsData?.data || [];
  const totalPosts = postsData?.pagination?.total ?? posts.length;
  const canShowMore = totalPosts > visibleCount;
  const categories = categoriesData?.data || [];

  const handleTogglePublish = (postId, currentStatus) => {
    togglePublishMutation.mutate(
      { postId, isPublished: !currentStatus },
      {
        onSuccess: () => {
          toast.success(
            currentStatus ? "Post unpublished successfully" : "Post published successfully"
          );
        },
        onError: () => {
          toast.error("Failed to update post status");
        },
      }
    );
  };

  const handleDeletePost = () => {
    deletePostMutation.mutate(deleteDialog.postId, {
      onSuccess: () => {
        setDeleteDialog({ open: false, postId: null, title: "" });
        toast.success("Post deleted successfully");
      },
      onError: () => {
        toast.error("Failed to delete post");
      },
    });
  };

  return (
    <div className="mx-auto w-full max-w-400 space-y-6 p-4 sm:space-y-8 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl md:text-4xl">
            Blog Posts
          </h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base md:text-lg">
            Manage your articles, news, and updates.
          </p>
        </div>
        <Link to="/dashboard/blogs/create">
          <Button size="lg" className="w-full px-6 text-base shadow-sm sm:w-auto cursor-pointer">
            <Plus className="mr-2 h-5 w-5" />
            Create New Post
          </Button>
        </Link>
      </div>

      {/* Filters Bar: Stacks on mobile, aligns perfectly to desktop row at lg breakpoint */}
      <div className="bg-card sticky top-0 z-10 flex flex-col gap-4 rounded-xl border p-4 shadow-sm sm:p-5 lg:flex-row lg:items-center">
        <div className="w-full lg:flex-1">
          <SearchInput
            isSearching={isSearching}
            handleSearch={handleSearch}
            placeholder="Search by title, excerpt, or date..."
            width="w-full"
            height="h-11"
          />
        </div>

        <div className="flex w-full flex-col flex-wrap items-center gap-3 sm:flex-row lg:w-auto lg:justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="lg"
                className="h-11 w-full justify-between border-dashed sm:w-auto sm:min-w-40 cursor-pointer">
                <div className="flex items-center">
                  <Filter className="text-muted-foreground mr-2 h-4 w-4" />
                  <span className="truncate">
                    {selectedCategory
                      ? categories.find((c) => c.id === parseInt(selectedCategory))?.name
                      : "All Categories"}
                  </span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setSelectedCategory("")}>
                All Categories
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {categories.map((category) => (
                <DropdownMenuItem
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id.toString())}>
                  {category.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="bg-border mx-1 hidden h-8 w-px sm:block" />

          <div className="bg-muted/50 flex w-full gap-1 rounded-lg p-1.5 sm:w-auto">
            {["all", "published", "draft"].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`cursor-pointer flex-1 rounded-md px-4 py-2 text-sm font-medium transition-all sm:flex-none ${filterStatus === status
                    ? "text-primary bg-white shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Grid: 1 col on mobile/tablet portrait, 2 cols on Desktop (lg), 3 on Large Desktop (2xl) */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-2 2xl:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex flex-col space-y-4">
              <Skeleton className="h-70 w-full rounded-2xl" />
              <div className="space-y-3 p-2">
                <Skeleton className="h-6 w-[70%]" />
                <Skeleton className="h-4 w-[40%]" />
              </div>
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-muted/10 animate-in fade-in-50 flex h-100 flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center sm:h-125 sm:p-8">
          <div className="bg-muted/50 mb-4 flex h-20 w-20 items-center justify-center rounded-full sm:mb-6 sm:h-24 sm:w-24">
            <FileText className="text-muted-foreground h-10 w-10 sm:h-12 sm:w-12" />
          </div>
          <h3 className="text-foreground text-xl font-semibold sm:text-2xl">No posts found</h3>
          <p className="text-muted-foreground mt-2 mb-6 max-w-md text-sm sm:mb-8 sm:text-base md:text-lg">
            {searchTerm || selectedCategory || filterStatus !== "all"
              ? "Try adjusting your search or filters to find what you're looking for."
              : "Get started by creating your first blog post to share with your audience."}
          </p>
          {searchTerm || selectedCategory || filterStatus !== "all" ? (
            <Button
              variant="outline"
              size="default"
              className="h-10 sm:h-11"
              onClick={() => {
                resetSearch();
                setSelectedCategory("");
                setFilterStatus("all");
              }}>
              Clear Filters
            </Button>
          ) : (
            <Link to="/dashboard/blogs/create">
              <Button size="default" className="h-10 sm:h-11">
                Create Post
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-2 2xl:grid-cols-3">
            {posts.map((post) => (
              <div
                key={post.id}
                className="group bg-card hover:border-primary/20 relative flex flex-col overflow-hidden rounded-2xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                {/* Image Area */}
                <div className="bg-muted relative aspect-video w-full overflow-hidden">
                  {post.featured_image ? (
                    <img
                      src={getImageUrl(post.featured_image)}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="bg-secondary/30 flex h-full w-full items-center justify-center">
                      <LayoutGrid className="text-muted-foreground/30 h-16 w-16" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-2 sm:top-4 sm:right-4">
                    <Badge
                      variant={post.is_published ? "default" : "secondary"}
                      className={`${post.is_published ? "bg-green-600 hover:bg-green-700" : "bg-white/90 text-gray-700"} border-0 px-2 py-0.5 text-xs shadow-md backdrop-blur-md sm:px-3 sm:py-1 sm:text-sm`}>
                      {post.is_published ? "Published" : "Draft"}
                    </Badge>
                  </div>
                </div>

                {/* Card Content */}
                <div className="flex flex-1 flex-col p-4 sm:p-6 lg:p-8">
                  <div className="mb-3 flex items-center justify-between sm:mb-4">
                    <Badge
                      variant="outline"
                      className="rounded-full border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-normal text-blue-700 sm:px-3 sm:py-1 sm:text-sm">
                      {post.category_name || "Uncategorized"}
                    </Badge>
                    <span className="text-muted-foreground flex items-center text-xs sm:text-sm">
                      <Clock className="mr-1 h-3 w-3 sm:mr-1.5 sm:h-4 sm:w-4" />
                      {post.reading_time || 5} min
                    </span>
                  </div>

                  <TextWithTooltip
                    text={post.title}
                    lineClamp="line-clamp-1"
                    className="text-card-foreground group-hover:text-primary mb-2 text-xl leading-tight font-bold transition-colors sm:mb-3 sm:text-2xl"
                  />
                  <TextWithTooltip
                    text={post.excerpt || "No description provided for this post."}
                    lineClamp="line-clamp-2"
                    className="text-muted-foreground mb-4 flex-1 text-sm sm:mb-6 sm:text-base"
                  />

                  <div className="mt-auto flex items-center justify-between border-t pt-4 sm:pt-5">
                    <span className="text-muted-foreground flex items-center text-xs sm:text-sm">
                      <Calendar className="mr-1.5 h-3 w-3 sm:mr-2 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">
                        {new Date(post.created_at).toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                      <span className="sm:hidden">
                        {new Date(post.created_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "2-digit",
                        })}
                      </span>
                    </span>

                    <div className="flex gap-2 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                      <Link to={`/dashboard/blogs/edit/${post.id}`}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-primary hover:bg-primary/10 h-9 w-9 cursor-pointer">
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Edit post</TooltipContent>
                        </Tooltip>
                      </Link>
                      <Tooltip>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground hover:bg-muted h-9 w-9 cursor-pointer">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleTogglePublish(post.id, post.is_published)}>
                            {post.is_published ? (
                              <>
                                <EyeOff className="mr-2 h-4 w-4" /> Unpublish
                              </>
                            ) : (
                              <>
                                <Eye className="mr-2 h-4 w-4" /> Publish
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            disabled={post.is_published}
                            onClick={(e) => {
                              if (post.is_published) {
                                e.preventDefault();
                                return;
                              }
                              setDeleteDialog({ open: true, postId: post.id, title: post.title });
                            }}
                            className={`${post.is_published
                                ? "cursor-not-allowed opacity-50"
                                : "text-red-600 focus:bg-red-50 focus:text-red-700"
                              }`}>
                            {post.is_published ? (
                              <>
                                <AlertCircle className="mr-2 h-4 w-4" /> Unpublish to Delete
                              </>
                            ) : (
                              <>
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </>
                            )}
                          </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <TooltipContent side="top">More actions</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {canShowMore && (
            <div className="flex justify-center pt-2 sm:pt-4">
              <Button
                className="cursor-pointer"
                type="button"
                variant="outline"
                size="lg"
                disabled={isFetching}
                onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}>
                {isFetching ? "Loading..." : "Show More"}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Delete Dialog */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => {
          if (!open) setDeleteDialog({ open: false, postId: null, title: "" });
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete
              <span className="text-foreground font-bold"> "{deleteDialog.title}"</span>? <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePost}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600">
              Delete Post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
